import React, { useState } from 'react';
import { Play } from 'lucide-react';
import Container from './Container';

interface ShowcaseItem {
  title: string;
  description: string;
  image: string;
  tags: string[];
}

const Showcase: React.FC = () => {
  const [activeFilter, setActiveFilter] = useState<string>('all');
  
  const showcaseItems: ShowcaseItem[] = [
    {
      title: 'Space Adventure',
      description: 'A 2D side-scrolling space shooter generated with AI commands.',
      image: 'https://images.pexels.com/photos/1670977/pexels-photo-1670977.jpeg',
      tags: ['ai-generated', 'action', '2d'],
    },
    {
      title: 'Fantasy RPG',
      description: 'Fantasy role-playing game built with the no-code editor.',
      image: 'https://images.pexels.com/photos/7887800/pexels-photo-7887800.jpeg',
      tags: ['no-code', 'rpg', '3d'],
    },
    {
      title: 'Puzzle Master',
      description: 'Complex puzzle game developed by a professional using GDK.',
      image: 'https://images.pexels.com/photos/1329644/pexels-photo-1329644.jpeg',
      tags: ['gdk', 'puzzle', '3d'],
    },
    {
      title: 'Racing Simulation',
      description: 'High-performance racing game built with both AI and GDK.',
      image: 'https://images.pexels.com/photos/8885024/pexels-photo-8885024.jpeg',
      tags: ['ai-generated', 'gdk', 'racing', '3d'],
    },
    {
      title: 'Platformer Adventure',
      description: 'Classic platformer created using the no-code editor.',
      image: 'https://images.pexels.com/photos/442576/pexels-photo-442576.jpeg',
      tags: ['no-code', 'platformer', '2d'],
    },
    {
      title: 'Strategy World',
      description: 'Complex strategy game built by a professional developer.',
      image: 'https://images.pexels.com/photos/8885170/pexels-photo-8885170.jpeg',
      tags: ['gdk', 'strategy', '3d'],
    },
  ];
  
  const filters = [
    { id: 'all', label: 'All Games' },
    { id: 'ai-generated', label: 'AI-Generated' },
    { id: 'no-code', label: 'No-Code' },
    { id: 'gdk', label: 'GDK' },
  ];
  
  const filteredItems = activeFilter === 'all'
    ? showcaseItems
    : showcaseItems.filter(item => item.tags.includes(activeFilter));
  
  return (
    <section id="showcase" className="bg-background py-16 md:py-24">
      <Container>
        <div className="mx-auto mb-12 max-w-2xl text-center">
          <h2 className="mb-4 text-3xl font-bold sm:text-4xl md:text-5xl">
            Game Showcase
          </h2>
          <p className="text-lg text-muted-foreground">
            Explore amazing games created with Wizantix.games. From AI-generated prototypes to professionally developed titles.
          </p>
        </div>
        
        <div className="mb-8 flex flex-wrap items-center justify-center gap-2">
          {filters.map((filter) => (
            <button
              key={filter.id}
              onClick={() => setActiveFilter(filter.id)}
              className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                activeFilter === filter.id
                  ? 'bg-primary text-white'
                  : 'bg-card text-foreground hover:bg-accent'
              }`}
            >
              {filter.label}
            </button>
          ))}
        </div>
        
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {filteredItems.map((item, index) => (
            <div
              key={index}
              className="group relative overflow-hidden rounded-xl border border-border bg-card transition-all duration-300 hover:shadow-md"
            >
              <div className="relative aspect-video overflow-hidden">
                <img
                  src={item.image}
                  alt={item.title}
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                  <div className="absolute inset-0 flex items-center justify-center">
                    <button className="flex h-12 w-12 items-center justify-center rounded-full bg-primary text-white shadow-lg transition-transform duration-300 hover:scale-110">
                      <Play className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="p-4">
                <h3 className="mb-1 text-xl font-semibold">{item.title}</h3>
                <p className="mb-3 text-sm text-muted-foreground">{item.description}</p>
                <div className="flex flex-wrap gap-2">
                  {item.tags.map((tag, tagIndex) => (
                    <span
                      key={tagIndex}
                      className="rounded-full bg-background px-2 py-1 text-xs font-medium text-muted-foreground"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </Container>
    </section>
  );
};

export default Showcase;